"use client";

import { createContext, useContext } from "react";

/**
 * How long a client-side navigation waits for a page's data before showing the page anyway, with
 * its own loading states where the data goes. Until then the current page stays, with RouteProgress.
 */
export const LOADER_CUTOFF_MS = 500;

/**
 * A page loader's result: the data (`ready`), or, on a client navigation whose data took longer
 * than the cut-off, the request still running (`pending`, settling to `ready` or `error` in place).
 */
export interface LoaderResult<T> {
  status: "ready" | "pending" | "error";
  data?: T;
  error?: unknown;
  /** Settles (never rejects) when a pending result does. */
  settled?: Promise<void>;
}

/**
 * Wraps a page loader's request. On the server (document loads) it always waits, so the HTML has
 * the data. In the browser it waits up to `cutoffMs`: data by then renders the finished page as
 * before; otherwise the navigation goes ahead with the request still running. A request that fails
 * before the cut-off throws from the loader, as without this wrapper.
 */
export const raceLoaderData = async <T>(
  request: Promise<T>,
  cutoffMs = LOADER_CUTOFF_MS,
): Promise<LoaderResult<T>> => {
  if (import.meta.env.SSR) return { status: "ready", data: await request };
  const result: LoaderResult<T> = { status: "pending" };
  result.settled = request.then(
    (data) => {
      result.status = "ready";
      result.data = data;
    },
    (error: unknown) => {
      result.status = "error";
      result.error = error;
    },
  );
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    result.settled,
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, cutoffMs);
    }),
  ]);
  clearTimeout(timer);
  if (result.status === "error") throw result.error;
  return result;
};

export const LoaderPendingContext = createContext(false);

/**
 * True inside a page whose loader data is still on its way (see `DeferredLoaderData`). Components
 * then show their loading state where the data goes and must not fetch it themselves: the page
 * receives the data as props when it arrives, without mounting again.
 */
export const useLoaderPending = () => useContext(LoaderPendingContext);
